import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE, FIRM_IMG_BASE } from "@config/env";
import { Modal, ModalFooter } from "@common/Modal";



const post = (ep, f) => { const fd=new FormData(); Object.entries(f).forEach(([k,v])=>fd.append(k,v)); return axios.post(`${API_BASE}${ep}`,fd).then(r=>r.data).catch(()=>null); };

const fmtDate = (d) => { if (!d) return ""; try { return new Date(d).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}); } catch { return d; } };

// Clean location string — remove blank segments like ", , SD" → "SD"
const cleanLoc = (loc) => {
  if (!loc) return "";
  return loc.split(",").map(s=>s.trim()).filter(Boolean).join(", ");
};

const JOB_TYPE = { "1":"Full-time", "2":"Part-time", "3":"Contract", "4":"Permanent" };
const TYPE_COLORS = { "1":{ bg:"#16A34A",color:"#fff" }, "2":{ bg:"#F59E0B",color:"#fff" }, "3":{ bg:"#2563EB",color:"#fff" }, "4":{ bg:"#7C3AED",color:"#fff" } };

function SkeletonCard() {
  return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:20, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ height:16, background:"var(--fc-border-light)", borderRadius:5, width:"50%", marginBottom:8 }} />
          <div style={{ display:"flex", gap:8 }}>
            <div style={{ height:11, background:"var(--fc-bg-light)", borderRadius:5, width:80 }} />
            <div style={{ height:11, background:"var(--fc-bg-light)", borderRadius:5, width:60 }} />
          </div>
        </div>
        <div style={{ width:48, height:48, borderRadius:9, background:"var(--fc-border-light)" }} />
      </div>
      <div style={{ height:11, background:"var(--fc-bg-light)", borderRadius:5, width:"90%", marginBottom:6 }} />
      <div style={{ height:11, background:"var(--fc-bg-light)", borderRadius:5, width:"70%" }} />
    </div>
  );
}

function ConfirmDialog({ title, message, onConfirm, onCancel, children }) {
  return (
    <Modal title={title} maxWidth={420}
      footer={
        <ModalFooter
          onCancel={onCancel}
          onConfirm={onConfirm}
          confirmTone="danger"
          confirmLabel="Yes, remove"
        />
      }>
      <p style={{ fontSize:13, color:"var(--fc-text-muted)" }}>{message}</p>
      {children}
    </Modal>
  );
}

export default function JobsPage() {
  const { user, userSlug } = useAuth();
  const navigate    = useNavigate();
  const location    = useLocation();
  const isMyJobs    = location.pathname.includes("my-posted-jobs") || location.pathname.includes("my-jobs");

  const [jobs,      setJobs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [tab,       setTab]       = useState(isMyJobs ? "mine" : "all");
  const [removeJob, setRemoveJob] = useState(null);
  const [removeNote,setRemoveNote]= useState("");
  const [removing,  setRemoving]  = useState(false);
  const [removeErr, setRemoveErr] = useState("");
  const [toast,     setToast]     = useState(null);
  const [page,      setPage]      = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalPages,setTotalPages]= useState(1);
  const PER_PAGE = 10;
  const filterTimer = useRef();

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const loadJobs = (filter, pageNum) => {
    if (!userSlug) return;
    setLoading(true);
    const ep = tab === "mine" ? "get-my-job-postings" : "get-latest-job-postings";
    post(ep, { user_slug: userSlug, search_text: filter, page: pageNum, per_page: PER_PAGE }).then(data => {
      const list = data?.job_list || data?.data?.job_list || data?.data || [];
      setJobs(Array.isArray(list) ? list : []);
      setTotalJobs(data?.pagination?.total ?? (Array.isArray(list) ? list.length : 0));
      setTotalPages(data?.pagination?.total_pages ?? 1);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // Tab change - reset to page 1 and fetch
  useEffect(() => {
    if (!userSlug) return;
    setPage(1);
    loadJobs(search, 1);
  }, [userSlug, tab]);

  // Page change - fetch that page (skip the initial mount, tab effect already did it)
  const didMountPage = useRef(false);
  useEffect(() => {
    if (!didMountPage.current) { didMountPage.current = true; return; }
    loadJobs(search, page);
  }, [page]);

  // Search change - debounced, reset to page 1 and fetch from the API
  const didMountSearch = useRef(false);
  useEffect(() => {
    if (!didMountSearch.current) { didMountSearch.current = true; return; }
    clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      if (page === 1) { loadJobs(search, 1); } else { setPage(1); }
    }, 400);
    return () => clearTimeout(filterTimer.current);
  }, [search]);

  const handleRemove = async () => {
    if (!removeNote.trim()) { setRemoveErr("Please enter a reason"); return; }
    setRemoving(true); setRemoveErr("");
    const res = await post("remove-job", { user_slug: userSlug, job_slug: removeJob.job_slug, reason: removeNote });
    setRemoving(false);
    if (res && Number(res.status) === 1) {
      showToast("Job removed successfully");
      setRemoveJob(null); setRemoveNote("");
      loadJobs(search, page);
    } else {
      setRemoveErr(res?.message || "Failed to remove job.");
    }
  };

  const canEdit = (job) => tab === 'mine' || String(user?.user_id) === String(job.created_by) || String(userSlug) === String(job.user_slug);

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:"var(--fc-text-primary)", margin:0 }}>
            {tab === "mine" ? "My Posted Jobs" : "Active Job Postings"}
          </h1>
          <p style={{ fontSize:13, color:"var(--fc-text-muted)", margin:"3px 0 0" }}>
            {tab === "mine" ? "Jobs you have posted" : "All jobs posted within your firm's network"}
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => navigate("/create-job")} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill">
            <i className="fa-solid fa-plus" style={{fontSize:11}} /> Create Job
          </button>
        </div>
      </div>

      {/* Tabs + Search */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <div style={{ display:"flex", gap:4, background:"var(--fc-border-light)", padding:3, borderRadius:10 }}>
          {[{ key:"all", label:"All Jobs" },{ key:"mine", label:"My Jobs" }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding:"6px 16px", borderRadius:8, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
                background: tab===t.key?"var(--fc-card-bg)":"transparent", color: tab===t.key?"var(--fc-text-primary)":"var(--fc-text-muted)",
                boxShadow: tab===t.key?"0 1px 4px rgba(0,0,0,0.08)":"none" }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--fc-card-bg)", border:"1.5px solid var(--fc-border)", borderRadius:10, padding:"8px 14px", flex:1, minWidth:200 }}>
          <i className="fa-solid fa-magnifying-glass" style={{ fontSize:12, color:"var(--fc-text-subtle)" }} />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by title, employer..."
            style={{ border:"none", outline:"none", fontSize:13, color:"var(--fc-text-primary)", flex:1, fontFamily:"inherit", background:"transparent" }} />
          {search && <button onClick={()=>setSearch("")} style={{border:"none",background:"none",cursor:"pointer",color:"var(--fc-text-subtle)",padding:0}}><i className="fa-solid fa-xmark" style={{fontSize:12}} /></button>}
        </div>
        {!loading && <span style={{ fontSize:12, color:"var(--fc-text-muted)", flexShrink:0 }}>{totalJobs} job{totalJobs!==1?"s":""}</span>}
      </div>

      {/* List */}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {loading ? (
          [...Array(4)].map((_,i) => <SkeletonCard key={i} />)
        ) : jobs.length === 0 ? (
          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"52px 24px", textAlign:"center" }}>
            <div style={{ width:50, height:50, borderRadius:13, background:"var(--fc-border-light)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
              <i className="fa-solid fa-briefcase" style={{ fontSize:22, color:"var(--fc-text-hint)" }} />
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:6 }}>
              {search ? `No results for "${search}"` : "No jobs found"}
            </div>
            <div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginBottom:16 }}>
              {tab === "mine" ? "Post your first job to see it here" : "No active jobs in your network"}
            </div>
            {!search && (
              <button onClick={() => navigate("/create-job")} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
                <i className="fa-solid fa-plus" style={{fontSize:11}} /> Create Job
              </button>
            )}
          </div>
        ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {jobs.map((job, i) => {
            const typeStyle = TYPE_COLORS[job.job_type] || { bg:"var(--fc-border-light)", color:"var(--fc-text-secondary)" };
            const isOwn = tab === "mine" || String(user?.user_id) === String(job.created_by) || String(userSlug) === String(job.user_slug);
            const COLORS = ["#3B82F6","#8B5CF6","#10B981","#F59E0B","#EF4444","#06B6D4","#EC4899"];
            const sc  = COLORS[i % COLORS.length];
            const loc = (job.location_requirement||"").split(",").map(s=>s.trim()).filter(Boolean).join(", ");
            return (
              <div key={job.job_slug||i}
                onClick={()=>navigate(`/job/details/${job.job_slug}`)}
                style={{ background:"var(--fc-card-bg)", borderRadius:10, border:"1px solid var(--fc-border)", padding:"10px 14px", cursor:"pointer", transition:"box-shadow 0.15s, border-color 0.15s" }}
                onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 4px 18px rgba(0,0,0,0.08)";e.currentTarget.style.borderColor="var(--fc-text-hint)";}}
                onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor="var(--fc-border)";}}>

                {/* Top row: serial + meta + logo */}
                <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                  {/* Serial */}
                  <div style={{ width:32,height:32,borderRadius:8,background:sc+"15",border:`1.5px solid ${sc}30`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <span style={{ fontSize:7,fontWeight:700,color:sc,opacity:0.7,lineHeight:1,textTransform:"uppercase" }}>No</span>
                    <span style={{ fontSize:15,fontWeight:900,color:sc,lineHeight:1 }}>{i+1}</span>
                  </div>

                  {/* Main content */}
                  <div style={{ flex:1, minWidth:0 }}>
                    {/* Title */}
                    <div style={{ fontSize:13,fontWeight:700,color:"var(--fc-text-primary)",lineHeight:1.3,marginBottom:3 }}>{job.job_title}</div>

                    {/* Meta row — prominent */}
                    <div style={{ display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",marginTop:4 }}>
                      {job.job_type && (
                        <span style={{ fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:6,background:typeStyle.bg,color:typeStyle.color,whiteSpace:"nowrap" }}>
                          {JOB_TYPE[job.job_type]||job.job_type}
                        </span>
                      )}
                      {job.created_on && (
                        <span style={{ fontSize:11,fontWeight:500,color:"var(--fc-text-muted)",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:4 }}>
                          <i className="fa-regular fa-calendar" style={{fontSize:10,color:"var(--fc-text-subtle)"}}/>
                          {fmtDate(job.created_on)}
                        </span>
                      )}
                      {loc && (
                        <span style={{ display:"inline-flex",alignItems:"center",gap:4,fontSize:11,fontWeight:500,color:"var(--fc-text-muted)",whiteSpace:"nowrap" }}>
                          <i className="fa-solid fa-location-dot" style={{fontSize:10,color:"var(--fc-text-subtle)"}}/>{loc}
                        </span>
                      )}
                      {job.employer_name && (
                        <span style={{ fontSize:11,fontWeight:500,color:"var(--fc-text-muted)",whiteSpace:"nowrap" }}>
                          · {job.employer_name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Firm logo — rectangular */}
                  {job.firm_logo && (
                    <div style={{ width:80,height:44,borderRadius:8,border:"1px solid var(--fc-border)",background:"var(--fc-card-bg)",flexShrink:0,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center" }}>
                      <img src={FIRM_IMG_BASE+job.firm_logo} alt=""
                        style={{ maxWidth:"100%",maxHeight:"100%",objectFit:"contain",padding:4 }}
                        onError={e=>e.target.parentElement.style.display="none"} />
                    </div>
                  )}
                </div>

                {/* Description */}
                {job.job_description && (
                  <p style={{ fontSize:12,color:"var(--fc-text-muted)",margin:"10px 0 0",lineHeight:1.6,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",paddingLeft:52 }}>
                    {job.job_description}
                  </p>
                )}

                {/* Bottom row: actions right-aligned */}
                {isOwn && (
                  <div style={{ display:"flex",justifyContent:"flex-end",gap:5,marginTop:8,paddingTop:8,borderTop:"1px solid var(--fc-border-light)" }} onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>navigate(`/job/edit/${job.job_slug}`)}
                      style={{ height:26,padding:"0 10px",borderRadius:6,border:"1px solid var(--fc-primary-border)",background:"var(--fc-primary-light)",color:"#2563EB",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,transition:"all 0.15s" }}
                      onMouseEnter={e=>{e.currentTarget.style.background="#2563EB";e.currentTarget.style.color="#fff";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="var(--fc-primary-light)";e.currentTarget.style.color="#2563EB";}}>
                      <i className="fa-solid fa-pen" style={{fontSize:9}}/>Edit
                    </button>
                    <button onClick={()=>{setRemoveJob(job);setRemoveNote("");setRemoveErr("");}}
                      style={{ height:26,padding:"0 10px",borderRadius:6,border:"1px solid var(--fc-red-border)",background:"var(--fc-red-light)",color:"#DC2626",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,transition:"all 0.15s" }}
                      onMouseEnter={e=>{e.currentTarget.style.background="#DC2626";e.currentTarget.style.color="#fff";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="var(--fc-red-light)";e.currentTarget.style.color="#DC2626";}}>
                      <i className="fa-solid fa-trash" style={{fontSize:9}}/>Remove
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:16, flexWrap:"wrap", gap:10 }}>
              <span style={{ fontSize:13, color:"var(--fc-text-muted)" }}>
                Showing <strong style={{color:"var(--fc-text-primary)"}}>{(page-1)*PER_PAGE+1}–{(page-1)*PER_PAGE+jobs.length}</strong> of <strong style={{color:"var(--fc-text-primary)"}}>{totalJobs}</strong> jobs
              </span>
              <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                  style={{ width:34,height:34,borderRadius:8,border:"1.5px solid var(--fc-border)",background:"var(--fc-card-bg)",cursor:page===1?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--fc-text-muted)",opacity:page===1?0.4:1,transition:"all 0.15s" }}
                  onMouseEnter={e=>page>1&&(e.currentTarget.style.borderColor="#2563EB")}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--fc-border)"}>
                  <i className="fa-solid fa-chevron-left" style={{fontSize:10}} />
                </button>
                {Array.from({length:totalPages},(_,i)=>i+1).filter(p=>p===1||p===totalPages||Math.abs(p-page)<=1).map((p,idx,arr)=>{
                  const showDot = idx>0 && p-arr[idx-1]>1;
                  return (
                    <span key={p} style={{display:"flex",alignItems:"center",gap:5}}>
                      {showDot && <span style={{color:"var(--fc-text-hint)",fontSize:14}}>···</span>}
                      <button onClick={()=>setPage(p)}
                        style={{ width:34,height:34,borderRadius:8,border:`1.5px solid ${p===page?"#2563EB":"var(--fc-border)"}`,background:p===page?"#2563EB":"var(--fc-card-bg)",color:p===page?"#fff":"var(--fc-text-muted)",fontSize:12,fontWeight:p===page?700:400,cursor:"pointer",transition:"all 0.15s" }}>
                        {p}
                      </button>
                    </span>
                  );
                })}
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                  style={{ width:34,height:34,borderRadius:8,border:"1.5px solid var(--fc-border)",background:"var(--fc-card-bg)",cursor:page===totalPages?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--fc-text-muted)",opacity:page===totalPages?0.4:1,transition:"all 0.15s" }}
                  onMouseEnter={e=>page<totalPages&&(e.currentTarget.style.borderColor="#2563EB")}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--fc-border)"}>
                  <i className="fa-solid fa-chevron-right" style={{fontSize:10}} />
                </button>
              </div>
            </div>
          )}
      </div>

      {/* Remove job modal */}
      {removeJob && (
        <ConfirmDialog
          title="Remove Job Posting?"
          message={`This will permanently remove "${removeJob.job_title}".`}
          onConfirm={handleRemove}
          onCancel={() => { setRemoveJob(null); setRemoveNote(""); setRemoveErr(""); }}>
          <div style={{ marginBottom:4 }}>
            <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>Reason for removal *</label>
            <input value={removeNote} onChange={e=>{setRemoveNote(e.target.value);setRemoveErr("");}} placeholder="Enter your reason..."
              disabled={removing}
              style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:`1.5px solid ${removeErr?"#DC2626":"var(--fc-border)"}`, borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", background:"var(--fc-card-bg)", color:"var(--fc-text-primary)" }} />
            {removeErr && <div style={{ fontSize:11, color:"#DC2626", marginTop:4 }}>{removeErr}</div>}
          </div>
        </ConfirmDialog>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999, borderRadius:12, padding:"13px 18px", display:"flex", alignItems:"center", gap:10, boxShadow:"0 8px 32px rgba(0,0,0,0.2)",
          background:toast.type==="error"?"var(--fc-red-light)":"#111827", border:toast.type==="error"?"1px solid var(--fc-red-border)":"none" }}>
          <div style={{ width:24, height:24, borderRadius:6, background:toast.type==="error"?"#DC2626":"#16A34A", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <i className={`fa-solid ${toast.type==="error"?"fa-xmark":"fa-check"}`} style={{fontSize:10,color:"#fff"}} />
          </div>
          <span style={{fontSize:13,fontWeight:600,color:toast.type==="error"?"#DC2626":"#fff"}}>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
